import {FC, ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {styled} from 'styled-components';

import {arrayJoin} from '@shared/lib/utils/array_utils';
import {normalizeString} from '@shared/lib/utils/string_utils';
import {removeUndefined} from '@shared/lib/utils/type_utils';
import {FrontendDoorMessage} from '@shared/models/api_models';

import {UnthemedNavLink} from '@shared_web/components/core/button';
import {showSuccess} from '@shared_web/components/core/notifications';
import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {Flex} from '@shared_web/components/core2/flex';
import {icon_delivery_truck} from '@shared_web/components/custom_icons/delivery_truck';
import {icon_ruler_horizontal} from '@shared_web/components/custom_icons/ruler_horizontal';
import {notifyError} from '@shared_web/lib/notification';

import {AddDoorFromCustomGroup} from '@mavismart_frontend/components/add_door_from_custom_group';
import {AlertSymbol} from '@mavismart_frontend/components/alert_symbol';
import {DateText} from '@mavismart_frontend/components/date_text';
import {DoorCustomAddress} from '@mavismart_frontend/components/door_custom_address';
import {DoorLabel} from '@mavismart_frontend/components/door_label';
import {Loader} from '@mavismart_frontend/components/loader';
import {RemoveDoorFromCustomGroup} from '@mavismart_frontend/components/remove_door_from_custom_group';
import {Title} from '@mavismart_frontend/components/title';
import {ToggleSwitch} from '@mavismart_frontend/components/toggle_switch';
import {translate, Translate} from '@mavismart_frontend/components/translate';
import {useSearchResultContext} from '@mavismart_frontend/contexts/search_result_context';
import {icon_calendar} from '@mavismart_frontend/icons/icon_calendar';
import {icon_door_cycles} from '@mavismart_frontend/icons/icon_door_cycles';
import {icon_door_specifications} from '@mavismart_frontend/icons/icon_door_specifications';
import {icon_home} from '@mavismart_frontend/icons/icon_home';
import {apiCall} from '@mavismart_frontend/lib/api';
import {TEXT_MAIN_COLOR} from '@mavismart_frontend/lib/constants';
import {DoorSearchField} from '@mavismart_frontend/lib/door_search';
import {
  GenericInfoBlock,
  GenericInfoBlockItem,
} from '@mavismart_frontend/pages/cells/generic_info_block';
import {getUserLanguage, getUserLocale, useAccount} from '@mavismart_frontend/stores/account_store';
import {fetchDoor, useDoor} from '@mavismart_frontend/stores/all_doors_store';

export type DisplayType = 'anomalies' | 'uses' | 'alerts' | 'messages' | 'editMessage';
type DisplayTypeWithoutMessages = Exclude<DisplayType, 'messages' | 'editMessage'>;
export const TITLE_DISPLAYED_TYPES: DisplayTypeWithoutMessages[] = ['uses', 'anomalies', 'alerts'];

interface DoorInfoProps {
  doorId: string;
  baseUrl: string;
}

export const DoorInfo: FC<DoorInfoProps> = ({doorId, baseUrl}) => {
  const [lastMessage, setLastMessage] = useState<FrontendDoorMessage | 'no-message' | undefined>();
  const door = useDoor(doorId);
  const [isNotificationsActivate, setIsNotificationsActivate] = useState(false);

  const account = useAccount();
  const locale = getUserLocale(account);

  const searchResult = useSearchResultContext();
  const doorSearchResult = useMemo(() => {
    return searchResult?.results.find(result => result.door.id === doorId);
  }, [searchResult, doorId]);

  // Helper function to highlight matching text in a value
  const highlightTextInValue = useCallback(
    (value: string | undefined, field: DoorSearchField): ReactNode => {
      if (!value) {
        return '';
      }
      const canHighlight =
        doorSearchResult !== undefined && searchResult?.searchQuery !== undefined;
      if (!canHighlight) {
        return value;
      }

      const match = doorSearchResult.matches.find(m => m.field === field);
      if (!match) {
        return value;
      }

      const searchQuery = searchResult.searchQuery;
      const searchStr = normalizeString(searchQuery);
      const valueStr = String(value);
      const normalizedValue = normalizeString(valueStr);
      const normalizedMatchIndex = normalizedValue.indexOf(searchStr);

      if (normalizedMatchIndex === -1) {
        return value;
      }

      // Find the match in the original string (case-insensitive)
      const searchLower = searchQuery.toLowerCase();
      const valueLower = valueStr.toLowerCase();
      let matchIndex = valueLower.indexOf(searchLower);

      // If direct search doesn't work, try to find it by character position approximation
      if (matchIndex === -1) {
        matchIndex = 0;
        for (let i = 0; i <= valueStr.length - searchQuery.length; i++) {
          const substr = valueStr.slice(i, i + searchQuery.length);
          if (normalizeString(substr) === searchStr) {
            matchIndex = i;
            break;
          }
        }
      }

      const beforeMatch = valueStr.slice(0, matchIndex);
      const matchText = valueStr.slice(matchIndex, matchIndex + searchQuery.length);
      const afterMatch = valueStr.slice(matchIndex + searchQuery.length);

      return (
        <>
          {beforeMatch}
          <HighlightSpan>{matchText}</HighlightSpan>
          {afterMatch}
        </>
      );
    },
    [doorSearchResult, searchResult?.searchQuery]
  );

  const lastMessageLink = useMemo(() => {
    if (lastMessage === undefined || lastMessage === 'no-message') {
      return '';
    }
    return `${baseUrl}/messages/${lastMessage.category}`;
  }, [baseUrl, lastMessage]);

  useEffect(() => {
    setLastMessage(undefined);
    apiCall('POST /list-door-messages', {id: doorId, limit: 1})
      .then(response => {
        if (response.messages.length === 0) {
          setLastMessage('no-message');
          return;
        }
        setLastMessage(response.messages[0]);
      })
      .catch(notifyError);
    fetchDoor(doorId);
  }, [doorId]);

  useEffect(() => {
    if (door?.notificationMode === 'force') {
      setIsNotificationsActivate(true);
    } else {
      setIsNotificationsActivate(false);
    }
  }, [door]);

  const widthItems = useMemo<GenericInfoBlockItem[]>(
    () => [
      {
        key: 'width',
        title: <Translate value="width" />,
        subtitle: door?.width ? door.width : <Translate value="no_width" />,
      },
      {
        key: 'width_ht',
        title: <Translate value="width_ht" />,
        subtitle: door?.widthHt ? door.widthHt : <Translate value="no_width_ht" />,
      },
    ],
    [door?.width, door?.widthHt]
  );

  const heightItems = useMemo<GenericInfoBlockItem[]>(
    () => [
      {
        key: 'height',
        title: <Translate value="height" />,
        subtitle: door?.height ? door.height : <Translate value="no_height" />,
      },
      {
        key: 'height_ht',
        title: <Translate value="height_ht" />,
        subtitle: door?.heightHt ? door.heightHt : <Translate value="no_height_ht" />,
      },
    ],
    [door?.height, door?.heightHt]
  );

  const datesItems = useMemo<GenericInfoBlockItem[]>(
    () => [
      {
        key: 'date_of_commission',
        title: <Translate value="date_of_commission" />,
        subtitle: door?.setupDate ? door.setupDate : <Translate value="no_date_of_commission" />,
      },
      {
        key: 'warranty_end_date',
        title: <Translate value="warranty_end_date" />,
        subtitle: door?.warrantyEndDate ? (
          door.warrantyEndDate
        ) : (
          <Translate value="no_warranty_end_date" />
        ),
      },
    ],
    [door?.setupDate, door?.warrantyEndDate]
  );

  const lastMessageItems = useMemo<GenericInfoBlockItem[]>(
    () => [
      {
        key: 'last_message',
        title: <Translate value="last_message" />,
        subtitle: lastMessage ? (
          lastMessage === 'no-message' ? (
            <Translate value="no_last_message" />
          ) : (
            <DateText date={new Date(lastMessage.ts)} />
          )
        ) : (
          <Translate value="loading" />
        ),
      },
    ],
    [lastMessage]
  );

  const cyclesItems = useMemo<GenericInfoBlockItem[]>(
    () => [
      {
        key: 'cycle_numbers',
        title: <Translate value="cycle_numbers" />,
        subtitle: (
          <div>
            <div>
              {door === undefined ? (
                <Translate value="loading" />
              ) : (
                (door.doorCycles ?? 0).toLocaleString(locale)
              )}
            </div>
          </div>
        ),
      },
    ],
    [door, locale]
  );

  const names = useMemo(() => {
    const name1 = door?.name1;
    const name2 = door?.name2;
    const name3 = door?.name3;
    const allNames = removeUndefined([name1, name2, name3]);
    return allNames.map(name => {
      // Check which field this name matches
      let field: DoorSearchField = 'name1';
      if (name === name1) field = 'name1';
      else if (name === name2) field = 'name2';
      else if (name === name3) field = 'name3';
      return {name, field};
    });
  }, [door?.name1, door?.name2, door?.name3]);

  const specificationsItems = useMemo<GenericInfoBlockItem[]>(
    () => [
      {
        key: 'specifications',
        title: <Translate value="specifications" />,
        subtitle:
          names.length === 0 ? (
            <Translate value="no_specifications" />
          ) : names.length === 1 ? (
            names[0]?.field ? (
              highlightTextInValue(names[0].name, names[0].field)
            ) : (
              names[0]?.name.toLocaleLowerCase(locale)
            )
          ) : (
            <div>
              {names.map(({name, field}) => (
                <div key={name}>
                  {field ? highlightTextInValue(name, field) : name.toLocaleLowerCase(locale)}
                </div>
              ))}
            </div>
          ),
      },
      {
        key: 'curtain_color',
        title: <Translate value="curtain_color" />,
        subtitle: door?.curtainColor ? (
          highlightTextInValue(door.curtainColor, 'curtainColor')
        ) : (
          <Translate value="no_curtain_color" />
        ),
      },
      {
        key: 'classification',
        title: <Translate value="classification" />,
        subtitle: door?.classification ? (
          highlightTextInValue(door.classification, 'classification')
        ) : (
          <Translate value="no_classification" />
        ),
      },
      {
        key: 'structure_color',
        title: <Translate value="structure_color" />,
        subtitle: door?.structureColor ? (
          highlightTextInValue(door.structureColor, 'structureColor')
        ) : (
          <Translate value="no_structure_color" />
        ),
      },
      {
        key: 'voltage',
        title: <Translate value="voltage" />,
        subtitle: door?.voltage ? (
          highlightTextInValue(door.voltage, 'voltage')
        ) : (
          <Translate value="no_voltage" />
        ),
      },
      {
        key: 'cabinet',
        title: <Translate value="cabinet" />,
        subtitle: door?.cabinet ? (
          highlightTextInValue(door.cabinet, 'cabinet')
        ) : (
          <Translate value="no_cabinet" />
        ),
      },
    ],
    [
      names,
      door?.curtainColor,
      door?.classification,
      door?.structureColor,
      door?.voltage,
      door?.cabinet,
      highlightTextInValue,
      locale,
    ]
  );

  const deliveredAddressItems = useMemo<GenericInfoBlockItem[]>(
    () => [
      {
        key: 'delivered_by_maviflex_at_address',
        title: <Translate value="delivered_by_maviflex_at_address" />,
        subtitle: (
          <div>
            <div>
              {arrayJoin<ReactNode>(
                [
                  highlightTextInValue(door?.customerName, 'customerName'),
                  highlightTextInValue(door?.address1, 'address1'),
                  highlightTextInValue(door?.address2, 'address2'),
                  highlightTextInValue(door?.address3, 'address3'),
                ].filter(str => str !== undefined && str !== ''),
                index => (
                  <br key={index} />
                )
              )}
            </div>
            <div>
              {highlightTextInValue(door?.postalCode, 'postalCode')}{' '}
              {highlightTextInValue(door?.city, 'city')}
            </div>
            <div>{highlightTextInValue(door?.country, 'country')}</div>
          </div>
        ),
      },
    ],
    [
      door?.customerName,
      door?.address1,
      door?.address2,
      door?.address3,
      door?.postalCode,
      door?.city,
      door?.country,
      highlightTextInValue,
    ]
  );

  const customAddressItems = useMemo<GenericInfoBlockItem[]>(
    () =>
      door === undefined
        ? []
        : [
            {
              key: 'custom_address',
              title: (
                <Flex $gap="s">
                  <Translate value="door_custom_address" />
                </Flex>
              ),
              subtitle: (
                <DoorCustomAddress
                  door={door}
                  searchQuery={searchResult?.searchQuery}
                  hasMatch={
                    doorSearchResult?.matches.some(m => m.field === 'customAddress') ?? false
                  }
                />
              ),
            },
          ],
    [door, searchResult?.searchQuery, doorSearchResult]
  );

  const handleNotificationsChange = useCallback(
    async (checked: boolean) => {
      setIsNotificationsActivate(checked);
      try {
        await apiCall('POST /update-door-notification-mode', {
          doorId,
          notificationMode: checked ? 'force' : 'default',
        });
        showSuccess(
          checked
            ? translate('notifications_activated', getUserLanguage(account))
            : translate('notifications_deactivated', getUserLanguage(account))
        );
        fetchDoor(doorId);
      } catch (error) {
        notifyError(error);
      }
    },
    [account, doorId]
  );

  if (door === undefined) {
    return <Loader />;
  }

  return (
    <MainContent>
      {door.name1 ? <Title name={`${door.name1.split(' ')[0]} - ${door.id}`} /> : <></>}
      <DoorLabel door={door} />
      <Grid>
        <GenericInfoBlock
          icon={<SvgIcon icon={icon_ruler_horizontal} size={38} color={TEXT_MAIN_COLOR} />}
          items={widthItems}
        />
        <GenericInfoBlock
          icon={<RotatedIcon icon={icon_ruler_horizontal} size={38} color={TEXT_MAIN_COLOR} />}
          items={heightItems}
        />
        <GenericInfoBlock
          icon={<SvgIcon icon={icon_calendar} size={22} color="transparent" />}
          items={datesItems}
        />
        <UnthemedNavLink to={lastMessageLink} style={{display: 'contents'}}>
          <GenericInfoBlock
            icon={
              <AlertSymbol
                level={lastMessage && lastMessage !== 'no-message' ? lastMessage.level : 'grey'}
              />
            }
            items={lastMessageItems}
          />
        </UnthemedNavLink>
      </Grid>
      <Section>
        <GenericInfoBlock
          icon={<SvgIcon icon={icon_door_cycles} size={22} />}
          items={cyclesItems}
        />
        <GenericInfoBlock
          icon={<SvgIcon icon={icon_door_specifications} size={24} color={TEXT_MAIN_COLOR} />}
          items={specificationsItems}
        />

        <GenericInfoBlock
          icon={<SvgIcon icon={icon_delivery_truck} size={32} color={TEXT_MAIN_COLOR} />}
          items={deliveredAddressItems}
        />

        <GenericInfoBlock
          icon={<SvgIcon icon={icon_home} size={22} color="transparent" />}
          items={customAddressItems}
        />
      </Section>

      <WrapperNotifications>
        <StyledMinWidth>
          <Translate value="enable_notifications_for_this_door" />
        </StyledMinWidth>

        <ToggleSwitch checked={isNotificationsActivate} onChange={handleNotificationsChange} />
      </WrapperNotifications>
      <WrapperAddOrRemove>
        <AddDoorFromCustomGroup doorId={door.id} />
        <RemoveDoorFromCustomGroup doorId={door.id} />
      </WrapperAddOrRemove>
    </MainContent>
  );
};

DoorInfo.displayName = 'DoorInfo';

const MainContent = styled.div`
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
  overflow-x: hidden;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
  margin: 5px 0;
  max-width: 100vw;
  box-sizing: border-box;
`;

export const IconCircle = styled.div`
  width: 38px;
  height: 38px;
  min-width: 38px;
  max-width: 38px;
  border-radius: 50%;
  /* background: #eef1f4; */
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Section = styled.div`
  margin-bottom: 5px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const WrapperNotifications = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  font-weight: 600;
`;

const RotatedIcon = styled(SvgIcon)`
  transform: rotate(90deg);
`;

const WrapperAddOrRemove = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
`;

const StyledMinWidth = styled.div`
  min-width: 250px;
`;

const HighlightSpan = styled.span`
  background-color: #ffff0077;
  color: #000000dd;
  border-radius: 3px;
  padding: 1px 0;
  font-weight: 600;
`;
